//@flow
import * as Immutable from 'immutable';

import {
    EntityImportTable,
    EntityImportHeader,
    EntityImportRow
} from 'common/models/flow/EntityImportTable';
import type {
    EntityImportTableModelType,
    EntityImportHeaderModelType,
    EntityImportRowModelType
} from 'common/models/flow/EntityImportTable';
import { memoized } from 'common/utils/Memoizer';

import I18nHelper from 'common/helpers/I18nHelper';
import isFunction from 'lodash/isFunction';

const translationsNamespace = ['validations', 'entityManagement'];
const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

class InternalEntityImportTable extends EntityImportTable {
    static fromJS(json: EntityImportTableModelType): InternalEntityImportTable {
        const state: Object = Object.assign({}, json);
        state.headers = Immutable.List(state.headers).map(item =>
            InternalEntityImportHeader.fromJS(item)
        );
        state.rows = Immutable.List(state.rows).map(item =>
            InternalEntityImportRow.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    getHeadersMap() {
        const { headers } = this;
        return headers.reduce((obj, header) => {
            obj[header.id] = header;
            return obj;
        }, {});
    }

    @memoized()
    getEditedTable(editedCells: ?Array<Object>): this {
        let table = this;
        let newRows = table.rows;
        if (editedCells && editedCells.length > 0) {
            newRows = newRows.map(row => {
                let newRow = row;
                editedCells.forEach(cell => {
                    if (cell.rowId === row.get('id')) {
                        if (newRow.get(cell.headerId) !== cell.value) {
                            newRow = newRow.set(cell.headerId, cell.value);
                            return cell;
                        }
                    }
                });
                return newRow;
            });
            table = table.setRows(newRows);
        }
        return table;
    }

    getHeadersForExport(): Immutable.List<string> {
        return this.headers.map(header => header.title);
    }

    getRowsForExport(): EntityImportRowModelType {
        const headersMap = this.getHeadersMap();
        return this.rows.map(row => {
            return row.mapKeys(key => {
                const header = headersMap[key];
                return header && header.title;
            });
        });
    }

    @memoized()
    getHeaderAccessor(id: string): ?Immutable.List<string> {
        const headerMap = this.getHeadersMap();
        const header = headerMap[id];
        return header && header.getAccessor();
    }

    getValidationErrors({
        uploadedEntities,
        defaultColumns,
        isIdAutoGenerated,
        entitySettings
    }: {
        uploadedEntities: Immutable.List<*>
    }): Array<string> {
        const validationErrors = [];
        let idValidationErrors = Immutable.Map();
        let requiredColumnTitles = Immutable.List();
        const { headers } = this;
        // 1. duplicate columns
        // 2. required columns
        // 3. duplicate ids for rows
        // 4. id validation (2 letters, duplicates)
        // 5. no rows

        const noRowsFound = this.rows.size === 0;
        // no rows
        if (noRowsFound) {
            validationErrors.push(nsTranslate('translations:noRowsFound'));
        }

        const groupedHeaders = headers.groupBy(header => header.id);
        const duplicateHeaderNames = groupedHeaders.reduce(
            (duplicateNames, currentGroup) => {
                if (currentGroup && currentGroup.size > 1) {
                    duplicateNames.push(currentGroup.get(0).title);
                }
                return duplicateNames;
            },
            []
        );
        // duplicate columns
        if (duplicateHeaderNames.length > 0) {
            validationErrors.push(
                nsTranslate('entityImport.duplicateColumns', {
                    duplicateHeaderNames: duplicateHeaderNames.join(', ')
                })
            );
            return validationErrors; // skip all other validations if duplicate columns
        }

        // required columns

        const headersMap = this.getHeadersMap();

        const requiredColumns = defaultColumns.filter(column => {
            const isRequired = isFunction(column.required)
                ? column.required({ entitySettings })
                : column.required;
            return isRequired;
        });
        requiredColumns.forEach(requiredColumn => {
            if (!headersMap[requiredColumn.id]) {
                requiredColumnTitles = requiredColumnTitles.push(
                    requiredColumn.title
                );
            }
        });
        if (requiredColumnTitles.size > 0) {
            validationErrors.push(
                nsTranslate('entityImport.requiredColumnsError', {
                    requiredColumns: requiredColumnTitles.join(', ')
                })
            );
        }
        // ID validations
        uploadedEntities.forEach((entity, index) => {
            const validatedEntity = entity.setAllValidationErrors();
            const idError =
                validatedEntity.getValidationMessage('id') ||
                validatedEntity.getValidationMessage('code');
            if (idError) {
                idValidationErrors = idValidationErrors.update(
                    idError,
                    (error = Immutable.Map()) =>
                        error.update('ids', (list = Immutable.List()) =>
                            list.push(index + 1)
                        )
                );
            }
        });

        if (idValidationErrors.size > 0) {
            idValidationErrors.forEach((error, message) => {
                const rowIds = error.get('ids');
                const rowIdsMessage = nsTranslate('entityImport.rowIdMessage', {
                    count: rowIds.length,
                    rowIds: rowIds.join(', ')
                });
                // for every ID validation message, shows list of row IDs
                validationErrors.push(`${message} (${rowIdsMessage})`);
            });
        }

        if (isIdAutoGenerated) {
            return validationErrors;
        }

        // duplicate IDs
        const groupedEntities = uploadedEntities.groupBy(entity => entity.id);
        const duplicateEntities = groupedEntities.reduce(
            (duplicateIds, entityGroup) => {
                const { id } = entityGroup.get(0);
                if (entityGroup && entityGroup.size > 1) {
                    duplicateIds.push(id);
                }
                // If already other validation errors are present and id is falsy, ignore duplication message
                if (!id && validationErrors.length) {
                    duplicateIds.pop();
                }
                return duplicateIds;
            },
            []
        );
        if (duplicateEntities.length > 0) {
            validationErrors.push(
                nsTranslate('entityImport.duplicateEntities', {
                    entityIds: duplicateEntities.join(', '),
                    count: duplicateEntities.length
                }) // duplicate entities message, shows list of entity IDs
            );
        }
        return validationErrors;
    }

    get headers(): Immutable.List<EntityImportHeader> {
        return this._state.get('headers');
    }

    get rows(): Immutable.List<EntityImportRow> {
        return this._state.get('rows');
    }
}

class InternalEntityImportHeader extends EntityImportHeader {
    static fromJS(
        json: EntityImportHeaderModelType
    ): InternalEntityImportHeader {
        const state: Object = Object.assign({}, json);
        state.accessor = state.accessor
            ? Immutable.List(state.accessor)
            : state.accessor;
        return new this(Immutable.Map(state));
    }

    getAccessor(): Immutable.List<string> {
        const { accessor, id } = this;
        return accessor || Immutable.List([id]);
    }

    getGroupAccessor(): ?Immutable.List<string> {
        // remove last item from accessor
        const groupAccesssor = this.getAccessor().pop();
        if (groupAccesssor.size > 0) {
            return groupAccesssor;
        }
    }
    getFieldAccessor(): string {
        // get last item from accessor
        return this.getAccessor().last();
    }

    get headers(): Immutable.List<EntityImportHeader> {
        return this._state.get('headers');
    }

    get rows(): Immutable.List<EntityImportRow> {
        return this._state.get('rows');
    }
}

class InternalEntityImportRow extends EntityImportRow {
    static fromJS(json: EntityImportRowModelType): InternalEntityImportRow {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): EntityImportRowModelType {
        return this.getState().toObject();
    }
}

export {
    InternalEntityImportTable as EntityImportTable,
    InternalEntityImportHeader as EntityImportHeader,
    InternalEntityImportRow as EntityImportRow
};
